import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  Table, Button, Modal, Form, Input, Select, Tag, Space, Spin, message, Avatar, Tooltip, Card, Row, Col, Tabs, Checkbox, Alert
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, CrownOutlined, TeamOutlined
} from '@ant-design/icons';
import { adminOperationsService } from '../../services/adminService';
import { hasPermission } from '../../utils/permissions';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const getToken = () => localStorage.getItem('admin_token') || '';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`,
});

interface PermissionDef {
  key: string;
  label: string;
}

interface PermissionModule {
  key: string;
  label: string;
  permissions: PermissionDef[];
}

interface PermissionCatalog {
  modules: PermissionModule[];
  roleDefaults: Record<string, string[]>;
}

interface StaffMember {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'manager' | 'staff';
  department?: string;
  position?: string;
  status: 'active' | 'inactive' | 'terminated';
  permissions: string[];
  last_login?: string;
  created_at: string;
}

const ROLE_COLORS: Record<string, string> = {
  manager: 'gold',
  admin: 'blue',
  staff: 'green',
};

const ROLE_ICONS: Record<string, React.ReactNode> = {
  manager: <CrownOutlined />,
  admin: <UserOutlined />,
  staff: <TeamOutlined />,
};

// Admin accounts are created directly in the database and are rejected by the
// API on create/promote, so they are never offered as a selectable role here.
const ROLE_OPTIONS = [
  { value: 'manager', label: 'Manager — Full access' },
  { value: 'reception', label: 'Reception — Front desk + bookings + rooms + laundry' },
  { value: 'cashier', label: 'Cashier — Billing + restaurant' },
  { value: 'kitchen', label: 'Kitchen — KOT queue only (no prices)' },
  { value: 'housekeeping', label: 'Housekeeping — Room workflows + laundry' },
  { value: 'staff', label: 'Staff — Basic read + edit' },
];

const StaffManagement: React.FC = () => {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
  const [permissionsVisible, setPermissionsVisible] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [form] = Form.useForm();

  // Permission catalog (modules + labels + role defaults) for the picker.
  const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);
  // Pending permission set for the Add/Edit modal. `permissionsDirty` stays
  // false until the picker is touched, so we omit `permissions` from the
  // payload and let the backend keep the role's default set.
  const [pickerPermissions, setPickerPermissions] = useState<string[]>([]);
  const [permissionsDirty, setPermissionsDirty] = useState(false);
  // Current role in the form, so the picker can prefill role defaults.
  const [formRole, setFormRole] = useState<string>('');

  // Resort Staff and Roles states
  const [roles, setRoles] = useState<any[]>([]);
  const [resortStaff, setResortStaff] = useState<any[]>([]);
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [resortStaffModalVisible, setResortStaffModalVisible] = useState(false);
  const [editingResortStaff, setEditingResortStaff] = useState<any | null>(null);

  const [roleForm] = Form.useForm();
  const [resortStaffForm] = Form.useForm();

  // Backend: staff:edit for create/edit, staff:delete for delete (admin-only
  // in the default role set). Hides the buttons the API would reject.
  const canEdit = hasPermission('staff:edit');
  const canDelete = hasPermission('staff:delete');

  const fetchResortData = async () => {
    setLoading(true);
    try {
      const [rolesRes, staffRes] = await Promise.all([
        adminOperationsService.getStaffRoles(),
        adminOperationsService.getStaffMembers(),
      ]);
      if (rolesRes?.success) setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
      if (staffRes?.success) setResortStaff(Array.isArray(staffRes.data) ? staffRes.data : []);
    } catch (err) {
      console.error('Error loading resort directory details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
    fetchResortData();
    fetchPermissionCatalog();
  }, []);

  const fetchPermissionCatalog = async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/permissions/catalog`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setCatalog(data.data);
    } catch {
      // Picker degrades to a read-only view when the catalog is unavailable.
      console.error('Error loading permission catalog');
    }
  };

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/staff`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setStaff(data.data);
      else message.error(data.message || 'Failed to load staff');
    } catch {
      message.error('Network error loading staff');
    } finally {
      setLoading(false);
    }
  };

  const roleDefaults = (role: string): string[] => catalog?.roleDefaults?.[role] || [];

  // A stored set equals "role defaults" when it contains exactly the default
  // keys (order-independent). Used to decide whether to send `permissions`.
  const sameSet = (a: string[], b: string[]) =>
    a.length === b.length && b.every((key) => a.includes(key));

  const handleAdd = () => {
    setEditingStaff(null);
    setFormRole('');
    setPickerPermissions([]);
    setPermissionsDirty(false);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (member: StaffMember) => {
    setEditingStaff(member);
    form.setFieldsValue({
      name: member.name,
      email: member.email,
      role: member.role,
      department: member.department,
      position: member.position,
      status: member.status,
    });
    setFormRole(member.role);
    setPickerPermissions(member.permissions || []);
    setPermissionsDirty(false);
    setModalVisible(true);
  };

  // Called when the role field changes: re-seed the picker with the new
  // role's defaults unless the admin already customized the set.
  const handleRoleChange = (role: string) => {
    setFormRole(role);
    if (!permissionsDirty) setPickerPermissions(roleDefaults(role));
  };

  const togglePermission = (key: string, checked: boolean) => {
    setPermissionsDirty(true);
    setPickerPermissions((prev) =>
      checked
        ? prev.includes(key) ? prev : [...prev, key]
        : prev.filter((p) => p !== key),
    );
  };

  const resetToRoleDefaults = () => {
    setPickerPermissions(roleDefaults(formRole));
    setPermissionsDirty(false);
  };

  const handleDelete = (member: StaffMember) => {
    Modal.confirm({
      title: `Remove ${member.name}?`,
      content: 'This will permanently delete this staff account.',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await fetch(`${API_BASE}/auth/staff/${member.id}`, {
            method: 'DELETE',
            headers: authHeaders(),
          });
          const data = await res.json();
          if (data.success) {
            message.success('Staff member removed');
            fetchStaff();
          } else {
            message.error(data.message || 'Delete failed');
          }
        } catch {
          message.error('Network error');
        }
      },
    });
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const url = editingStaff
        ? `${API_BASE}/auth/staff/${editingStaff.id}`
        : `${API_BASE}/auth/staff`;

      const method = editingStaff ? 'PUT' : 'POST';
      // Only send `permissions` when the picker was actually touched — an
      // omitted field lets the backend fall back to the role's default set
      // (create) or keep the existing set (update without a role change).
      const payload: Record<string, unknown> = { ...values };
      if (permissionsDirty) payload.permissions = pickerPermissions;

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) {
        message.success(editingStaff ? 'Staff updated' : 'Staff member added');
        setModalVisible(false);
        setPermissionsDirty(false);
        fetchStaff();
      } else {
        message.error(data.message || 'Operation failed');
      }
    } catch {
      message.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Staff Member',
      key: 'name',
      render: (_: any, record: StaffMember) => (
        <Space>
          <Avatar style={{ backgroundColor: ROLE_COLORS[record.role] + '33', color: ROLE_COLORS[record.role] }}>
            {record.name[0].toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.email}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag icon={ROLE_ICONS[role]} color={ROLE_COLORS[role]} style={{ textTransform: 'capitalize' }}>
          {role}
        </Tag>
      ),
    },
    {
      title: 'Department / Position',
      key: 'dept',
      render: (_: any, r: StaffMember) => (
        <div>
          <div>{r.department || <span style={{ color: '#ccc' }}>—</span>}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{r.position || ''}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : status === 'inactive' ? 'orange' : 'red'} style={{ textTransform: 'capitalize' }}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Permissions',
      key: 'permissions',
      render: (_: any, record: StaffMember) => {
        const defaults = catalog?.roleDefaults?.[record.role] || [];
        const isCustom = !!catalog && !sameSet(record.permissions || [], defaults);
        return (
          <Space size={4}>
            <Button
              size="small"
              type="dashed"
              onClick={() => { setSelectedPermissions(record.permissions || []); setPermissionsVisible(true); }}
            >
              {record.permissions?.length || 0} permissions
            </Button>
            {isCustom && <Tag color="purple" style={{ margin: 0 }}>custom</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (date?: string) => date
        ? dayjs(date).format('MMM D, YYYY h:mm A')
        : <span style={{ color: '#ccc' }}>Never</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: StaffMember) => (
        <Space>
          {canEdit && (
            <Tooltip title="Edit">
              <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete">
              <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(record)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'users',
      label: 'System Access (Admin Users)',
      children: (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24, marginTop: 16 }}>
            <div>
              <h3 style={{ margin: 0 }}>System Users</h3>
              <p style={{ color: '#666', margin: 0 }}>Accounts with credentials permitted to access and operate this admin panel.</p>
            </div>
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Add System User
              </Button>
            )}
          </div>
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={staff}
              rowKey="id"
              pagination={{ pageSize: 15, showTotal: t => `Total ${t} users` }}
            />
          </Spin>
        </div>
      )
    },
    {
      key: 'resort',
      label: 'Resort Directory & Roles',
      children: (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24, marginTop: 16 }}>
            <div>
              <h3 style={{ margin: 0 }}>Resort Staff & Custom Roles</h3>
              <p style={{ color: '#666', margin: 0 }}>Manage resort departments, roster roles, permissions, and directory details.</p>
            </div>
            <Space>
              <Button type="default" icon={<PlusOutlined />} onClick={() => setRoleModalVisible(true)}>
                Create Custom Role
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingResortStaff(null); resortStaffForm.resetFields(); setResortStaffModalVisible(true); }}>
                Add Resort Staff Member
              </Button>
            </Space>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={10}>
              <Card title="Custom Operational Roles" size="small">
                <Table
                  rowKey="id"
                  dataSource={roles}
                  columns={[
                    { title: 'Code', dataIndex: 'code', key: 'code', width: 120, render: (c) => <Tag color="blue" style={{ fontSize: 11 }}>{c}</Tag> },
                    { title: 'Name', dataIndex: 'name', key: 'name', width: 130, render: (n) => <span style={{ fontWeight: 600 }}>{n}</span> },
                    {
                      title: 'Permissions',
                      dataIndex: 'permissions',
                      key: 'permissions',
                      render: (p: string[]) => (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {(p || []).map(pItem => (
                            <Tag key={pItem} style={{ margin: 0, fontSize: 11 }}>{pItem}</Tag>
                          ))}
                        </div>
                      )
                    }
                  ]}
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} lg={14}>
              <Card title="Resort Staff Directory" size="small">
                <Table
                  rowKey="id"
                  dataSource={resortStaff}
                  columns={[
                    {
                      title: 'Staff Member',
                      key: 'name',
                      render: (_: any, r: any) => (
                        <Space>
                          <Avatar style={{ backgroundColor: '#b79a6815', color: '#b79a68' }}>
                            {r.full_name ? r.full_name[0].toUpperCase() : 'S'}
                          </Avatar>
                          <div>
                            <div style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{r.full_name}</div>
                            <div style={{ fontSize: 11, color: '#999', whiteSpace: 'nowrap' }}>{r.email}</div>
                          </div>
                        </Space>
                      )
                    },
                    { title: 'Phone', dataIndex: 'phone', key: 'phone', width: 130, render: (p) => p || <span style={{ color: '#ccc' }}>—</span> },
                    { title: 'Department', dataIndex: 'department', key: 'department', width: 110, render: (d) => d || <span style={{ color: '#ccc' }}>—</span> },
                    { title: 'Role', dataIndex: 'role_name', key: 'role_name', width: 110, render: (rn) => <Tag color="purple">{rn || 'General Staff'}</Tag> },
                    {
                      title: 'Actions',
                      key: 'actions',
                      width: 70,
                      render: (_: any, r: any) => (
                        <Space>
                          <Button size="small" icon={<EditOutlined />} onClick={() => {
                            setEditingResortStaff(r);
                            resortStaffForm.setFieldsValue({
                              full_name: r.full_name,
                              email: r.email,
                              phone: r.phone,
                              department: r.department,
                              role_id: r.role_id,
                            });
                            setResortStaffModalVisible(true);
                          }} />
                        </Space>
                      )
                    }
                  ]}
                  size="small"
                  scroll={{ x: 'max-content' }}
                />
              </Card>
            </Col>
          </Row>
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>Staff & Directory Management</h1>
        <p style={{ color: '#666', margin: 0 }}>Administer system credentials, custom operational roles, and team directories.</p>
      </div>

      <Tabs defaultActiveKey="users" items={tabItems} />

      {/* Add / Edit System User Modal */}
      <Modal
        title={editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={640}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Full Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Jane Doe" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true }, { type: 'email' }]}>
            <Input placeholder="jane@adamspalm.com" disabled={!!editingStaff} />
          </Form.Item>
          {!editingStaff && (
            <Form.Item name="password" label="Password" rules={[{ required: true, min: 8 }]}>
              <Input.Password placeholder="Min 8 characters" />
            </Form.Item>
          )}
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true }]}
          >
            <Select
              options={ROLE_OPTIONS}
              onChange={handleRoleChange}
              placeholder="Select a role"
            />
          </Form.Item>
          <Form.Item name="department" label="Department">
            <Input placeholder="e.g. Front Desk" />
          </Form.Item>
          <Form.Item name="position" label="Position">
            <Input placeholder="e.g. Receptionist" />
          </Form.Item>
          {editingStaff && (
            <Form.Item name="status" label="Status">
              <Select options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'terminated', label: 'Terminated' },
              ]} />
            </Form.Item>
          )}

          {/* Permission picker — grouped checkboxes seeded from the role defaults. */}
          <Form.Item label={
            <Space>
              <span>Permissions</span>
              {permissionsDirty && <Tag color="purple" style={{ margin: 0 }}>custom</Tag>}
            </Space>
          }>
            {!catalog ? (
              <Spin size="small" />
            ) : (
              <div>
                <Space style={{ marginBottom: 8 }} wrap>
                  <span style={{ fontSize: 12, color: '#999' }}>
                    {pickerPermissions.length} selected
                    {formRole ? ` · role default: ${roleDefaults(formRole).length}` : ''}
                  </span>
                  <Button size="small" onClick={resetToRoleDefaults} disabled={!formRole}>
                    Reset to role default
                  </Button>
                </Space>
                <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: 12 }}>
                  {catalog.modules.map((mod) => (
                    <div key={mod.key} style={{ marginBottom: 12 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{mod.label}</div>
                      {mod.permissions.map((perm) => (
                        <Checkbox
                          key={perm.key}
                          checked={pickerPermissions.includes(perm.key)}
                          onChange={(e) => togglePermission(perm.key, e.target.checked)}
                          style={{ display: 'block', marginLeft: 8, marginBottom: 4 }}
                        >
                          <span style={{ fontSize: 13 }}>{perm.label}</span>
                          <span style={{ fontSize: 11, color: '#bbb', marginLeft: 6 }}>{perm.key}</span>
                        </Checkbox>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Custom Role Modal */}
      <Modal
        title="Create Custom Operational Role"
        open={roleModalVisible}
        onCancel={() => setRoleModalVisible(false)}
        onOk={() => roleForm.submit()}
        confirmLoading={loading}
      >
        <Form form={roleForm} layout="vertical" onFinish={async (values) => {
          setLoading(true);
          try {
            const res = await adminOperationsService.createStaffRole({
              code: values.code,
              name: values.name,
            });
            if (res.success) {
              message.success('Custom operational role created successfully');
              setRoleModalVisible(false);
              roleForm.resetFields();
              fetchResortData();
            } else {
              message.error(res.message || 'Failed to create role');
            }
          } catch {
            message.error('Failed to create role');
          } finally {
            setLoading(false);
          }
        }}>
          <Form.Item name="code" label="Role Code" rules={[{ required: true, message: 'Please specify a role code' }]}>
            <Input placeholder="e.g. housekeeping" />
          </Form.Item>
          <Form.Item name="name" label="Role Name" rules={[{ required: true, message: 'Please specify a role name' }]}>
            <Input placeholder="e.g. Housekeeping Lead" />
          </Form.Item>
          <Alert
            type="info"
            showIcon
            message="Directory roles are display-only"
            description="Operational roles label the resort directory. Panel access is granted per user with the permission picker on the System Access tab."
          />
        </Form>
      </Modal>

      {/* Add / Edit Resort Staff Modal */}
      <Modal
        title={editingResortStaff ? 'Edit Resort Staff Member' : 'Add Resort Staff Member'}
        open={resortStaffModalVisible}
        onCancel={() => setResortStaffModalVisible(false)}
        onOk={() => resortStaffForm.submit()}
        confirmLoading={loading}
      >
        <Form form={resortStaffForm} layout="vertical" onFinish={async (values) => {
          setLoading(true);
          try {
            if (editingResortStaff) {
              const res = await adminOperationsService.updateStaffMember(editingResortStaff.id, values);
              if (res.success) {
                message.success('Resort staff member updated successfully');
                setResortStaffModalVisible(false);
                fetchResortData();
              } else {
                message.error(res.message || 'Failed to update staff member');
              }
            } else {
              const res = await adminOperationsService.createStaffMember(values);
              if (res.success) {
                message.success('Resort staff member registered successfully');
                setResortStaffModalVisible(false);
                resortStaffForm.resetFields();
                fetchResortData();
              } else {
                message.error(res.message || 'Failed to create staff member');
              }
            }
          } catch {
            message.error('Operation failed');
          } finally {
            setLoading(false);
          }
        }}>
          <Form.Item name="full_name" label="Full Name" rules={[{ required: true, message: 'Please specify a name' }]}>
            <Input placeholder="e.g. John Doe" />
          </Form.Item>
          <Form.Item name="email" label="Email Address" rules={[{ required: true, type: 'email', message: 'Please specify a valid email' }]}>
            <Input placeholder="e.g. john@resort.com" />
          </Form.Item>
          <Form.Item name="phone" label="Phone Number">
            <Input placeholder="e.g. +94 77 123 4567" />
          </Form.Item>
          <Form.Item name="department" label="Department">
            <Input placeholder="e.g. Housekeeping" />
          </Form.Item>
          <Form.Item name="role_id" label="Assigned Operational Role" rules={[{ required: true, message: 'Please assign a role' }]}>
            <Select
              placeholder="Select an operational role"
              options={(roles || []).map((role) => ({
                value: role.id,
                label: `${role.name} (${role.code})`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Permissions Viewer Modal */}
      <Modal
        title="Role Permissions"
        open={permissionsVisible}
        onCancel={() => setPermissionsVisible(false)}
        footer={<Button onClick={() => setPermissionsVisible(false)}>Close</Button>}
      >
        <Space wrap>
          {selectedPermissions.map(p => (
            <Tag key={p} color="blue">{p}</Tag>
          ))}
        </Space>
      </Modal>
    </div>
  );
};

export default StaffManagement;
